import { useEffect, useRef, useState } from 'react';
import { Button, Field, Radio } from '@aigovops/design-system';

type QuickPick = '7' | '14' | '30' | 'custom';

/**
 * Snooze picker (PR-P3-2 / UC-4 R1 "snooze + subscription in rail").
 * Quick-pick radio tiles (7/14/30 days) plus a custom date fallback.
 * Same native `<dialog>` + `showModal()` pattern as ConfirmDialog.
 */
export function SnoozeDialog({
  open,
  onClose,
  onConfirm,
  busy,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (until: Date) => void;
  busy?: boolean;
  error?: string | null;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [choice, setChoice] = useState<QuickPick>('7');
  const [customDate, setCustomDate] = useState('');

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Reset to the default pick each time the dialog reopens, so a prior
  // custom date doesn't silently carry over to the next alert.
  useEffect(() => {
    if (open) {
      setChoice('7');
      setCustomDate('');
    }
  }, [open]);

  function resolveUntil(): Date | null {
    if (choice === 'custom') {
      if (!customDate) return null;
      const d = new Date(`${customDate}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return new Date(Date.now() + Number(choice) * 24 * 60 * 60 * 1000);
  }

  const until = resolveUntil();
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  return (
    <dialog
      ref={dialogRef}
      aria-label="Snooze alert"
      onClose={onClose}
      onCancel={onClose}
      // See ConfirmDialog.tsx — showModal() doesn't give click-outside
      // for free, this wires it explicitly.
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-elevated)',
        color: 'var(--text)',
        padding: 'var(--space-4)',
        maxWidth: '24rem',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <strong style={{ fontSize: 'var(--font-size-2xs)' }}>Snooze until…</strong>
      {error ? (
        <p role="alert" style={{ margin: 0, color: 'var(--critical)', fontSize: 'var(--font-size-3xs)' }}>
          {error}
        </p>
      ) : null}
      <div role="radiogroup" aria-label="Snooze duration" style={{ display: 'grid', gap: '2px' }}>
        <Radio
          name="snooze-choice"
          label="1 week"
          checked={choice === '7'}
          onChange={() => setChoice('7')}
        />
        <Radio
          name="snooze-choice"
          label="2 weeks"
          checked={choice === '14'}
          onChange={() => setChoice('14')}
        />
        <Radio
          name="snooze-choice"
          label="1 month"
          checked={choice === '30'}
          onChange={() => setChoice('30')}
        />
        <Radio
          name="snooze-choice"
          label="Custom date"
          checked={choice === 'custom'}
          onChange={() => setChoice('custom')}
        />
      </div>
      {choice === 'custom' ? (
        <Field id="snooze-custom-date" label="Wake on">
          <input
            id="snooze-custom-date"
            type="date"
            min={tomorrow}
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            style={{
              width: '100%',
              minHeight: 'var(--min-touch-target)',
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--border-strong)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
            }}
          />
        </Field>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => until && onConfirm(until)}
          disabled={!until || busy}
        >
          {busy ? 'Snoozing…' : 'Snooze'}
        </Button>
      </div>
      </div>
    </dialog>
  );
}
